import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { HeroHeaderLogo } from "@/components/dataTable";
import { useCurrentUser, userHasGroup, userIsBuero, userIsManagement } from "@/lib/auth";
import { useRealtime } from "@/lib/useRealtime";
import { ApiError } from "@/lib/apiClient";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { HERO_PROJECT_BASE } from "@/lib/home";
import { openOrderFolder } from "@/lib/projects";
import {
  listAngebotsanfragen,
  type AgaListItem,
  type AgaStatus,
} from "@/lib/angebotsanfragen";
import { AngebotDialog } from "@/pages/angebotsanfragen/AngebotDialog";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import { MaDialog } from "@/pages/material/MaDialog";
import "@/pages/material/materialMobile.css";

const STATUS_FILTER_OPTIONS: { value: AgaStatus | ""; label: string }[] = [
  { value: "", label: "Alle" },
  { value: "offen", label: "Offen" },
  { value: "in_bearbeitung", label: "In Bearbeitung" },
  { value: "erledigt", label: "Erledigt" },
  { value: "abgelehnt", label: "Abgelehnt" },
];

const STATUS_BADGE: Record<AgaStatus, string> = {
  offen: "bg-status-orange/15 text-status-orange",
  in_bearbeitung: "bg-accent-soft text-accent",
  erledigt: "bg-status-green/15 text-status-green",
  abgelehnt: "bg-fill-tertiary text-label-secondary",
};

const STATUS_LABEL: Record<AgaStatus, string> = {
  offen: "Offen",
  in_bearbeitung: "In Bearbeitung",
  erledigt: "Erledigt",
  abgelehnt: "Abgelehnt",
};

function formatDate(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function AgaListProjectActions({ item }: { item: AgaListItem }) {
  const { user } = useCurrentUser();
  const showFolderOpen = userIsBuero(user) && isElectronWithOpenFolder();
  const hasFolder = Boolean(
    (item.ordnerpfad || item.sharepoint_path || "").trim(),
  );
  const heroProjectId = item.hero_project_id;

  if (!item.auftrag_id) return null;

  return (
    <div
      className="flex shrink-0 items-center gap-1"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {showFolderOpen ? (
        hasFolder ? (
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-separator bg-card text-label transition hover:bg-fill-tertiary"
            title="Ordner öffnen"
            aria-label="Ordner öffnen"
            onClick={() =>
              void openOrderFolder({
                id: item.auftrag_id as number,
                ordnerpfad: item.ordnerpfad,
                sharepoint_path: item.sharepoint_path,
              })
            }
          >
            <span className="text-[1.15rem] leading-none" aria-hidden>
              📂
            </span>
          </button>
        ) : (
          <span
            title="Kein Projektordner hinterlegt"
            className="inline-flex h-8 w-8 cursor-default items-center justify-center rounded-md border border-separator bg-card opacity-50"
          >
            <span className="text-[1.15rem] leading-none grayscale" aria-hidden>
              📂
            </span>
          </span>
        )
      ) : null}
      {heroProjectId ? (
        <a
          href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroProjectId))}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Hero-Projekt öffnen"
          aria-label="Hero-Projekt öffnen"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-separator bg-card transition hover:bg-fill-tertiary"
        >
          <HeroHeaderLogo className="h-[18px] w-auto object-contain" />
        </a>
      ) : (
        <span
          title="Kein Hero-Projekt hinterlegt"
          className="inline-flex h-8 w-8 cursor-default items-center justify-center rounded-md border border-separator bg-card opacity-50"
        >
          <HeroHeaderLogo className="h-[18px] w-auto object-contain grayscale" />
        </span>
      )}
    </div>
  );
}

export function AngebotsanfragenIndexPage() {
  const { user } = useCurrentUser();
  const canProcess = useMemo(() => {
    if (!user) return false;
    return (
      userHasGroup(user, "App-Admins") ||
      userIsBuero(user) ||
      userIsManagement(user)
    );
  }, [user]);

  const [items, setItems] = useState<AgaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<AgaStatus | "">("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [maDialogOpen, setMaDialogOpen] = useState(false);
  const [activeMaId, setActiveMaId] = useState<number | null>(null);
  const documentEditor = useDocumentEditor();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  // Deep-Link: /angebotsanfragen?id=123 öffnet direkt die Anfrage.
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (!idParam) return;
    const parsed = Number(idParam);
    if (Number.isFinite(parsed) && parsed > 0) {
      setActiveId(parsed);
      setDialogOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
    // Nur einmal beim Mount / bei id-Wechsel ausführen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const list = await listAngebotsanfragen({
        status: statusFilter || undefined,
        q: debouncedSearch || undefined,
      });
      setItems(list || []);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Liste konnte nicht geladen werden.",
      );
      if (!options?.silent) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime-Änderungen still einpflegen – die Liste bleibt dabei stehen.
  useRealtime({
    scopes: ["angebotsanfragen:list", "domain:angebotsanfragen"],
    domains: ["angebotsanfragen"],
    events: ["angebotsanfragen_changed"],
    onChange: () => void reload({ silent: true }),
    fallbackIntervalMs: 180_000,
  });

  function openNew() {
    setActiveId(null);
    setDialogOpen(true);
  }
  function openExisting(id: number) {
    setActiveId(id);
    setDialogOpen(true);
  }
  function closeDialog() {
    setDialogOpen(false);
    setActiveId(null);
  }
  function openMa(id: number) {
    setActiveMaId(id);
    setMaDialogOpen(true);
  }
  function openAngebot(docId: number) {
    documentEditor.open({
      docId,
      onClosed: () => void reload(),
    });
  }

  return (
    <AppShell
      title="Angebotsanfragen"
      className="material-mobile-shell material-mobile-shell--with-fab"
      subtitle={
        canProcess
          ? "Übersicht aller Angebotsanfragen zur Bearbeitung."
          : "Stelle eine Angebotsanfrage zu einem Projekt ein – das Büro kümmert sich."
      }
      actions={
        <div className="hidden flex-wrap gap-2 sm:flex">
          <Button onClick={openNew}>+ Neue Anfrage</Button>
        </div>
      }
    >
      <section className="ma-filters rounded-ios border border-separator bg-card p-4 shadow-ios max-sm:border-none max-sm:bg-transparent max-sm:p-0 max-sm:shadow-none">
        <div
          className="inline-flex flex-wrap gap-1 rounded-full bg-fill-tertiary p-1 ring-1 ring-separator/35"
          role="group"
          aria-label="Status filtern"
        >
          {STATUS_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value || "all"}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              className={clsx(
                "rounded-full px-3 py-1.5 text-sm font-semibold transition",
                statusFilter === option.value
                  ? "bg-card text-label shadow-sm ring-1 ring-separator/40"
                  : "text-label-secondary hover:text-label",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 max-sm:flex-col max-sm:items-stretch">
          <input
            type="search"
            placeholder="Suchen (Nummer, Projekt, Ansprechpartner, Text …)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="eh-input flex-1 min-w-[14rem]"
          />
        </div>
      </section>

      {error && (
        <Alert variant="error" title="Liste konnte nicht geladen werden">
          {error}
        </Alert>
      )}

      <section>
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center text-sm text-label-secondary shadow-ios">
            Keine Angebotsanfragen.
            <div className="mt-3">
              <Button onClick={openNew}>+ Neue Anfrage</Button>
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => openExisting(item.id)}
                  className="w-full rounded-ios border border-separator bg-card p-4 text-left shadow-ios transition hover:border-accent/60"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="font-semibold text-label">{item.aga_nummer}</span>
                      {item.source_ma_id && item.source_ma_nummer ? (
                        <span
                          role="link"
                          tabIndex={0}
                          className="inline-flex max-w-full cursor-pointer items-center truncate rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent hover:underline"
                          title={`Materialanforderung ${item.source_ma_nummer}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            openMa(item.source_ma_id as number);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              e.preventDefault();
                              openMa(item.source_ma_id as number);
                            }
                          }}
                        >
                          MA · {item.source_ma_nummer}
                        </span>
                      ) : null}
                      {item.gd_angebot_id ? (
                        <span
                          role="link"
                          tabIndex={0}
                          className="inline-flex max-w-full cursor-pointer items-center truncate rounded-md border border-status-green/35 bg-status-green/10 px-2 py-0.5 text-xs font-medium text-status-green hover:underline"
                          title={
                            item.gd_angebot_nummer
                              ? `Angebot ${item.gd_angebot_nummer} öffnen`
                              : "Angebot öffnen"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            openAngebot(item.gd_angebot_id as number);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              e.preventDefault();
                              openAngebot(item.gd_angebot_id as number);
                            }
                          }}
                        >
                          Angebot
                          {item.gd_angebot_nummer ? ` · ${item.gd_angebot_nummer}` : ""}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <AgaListProjectActions item={item} />
                      <span
                        className={clsx(
                          "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                          STATUS_BADGE[item.status],
                        )}
                      >
                        {STATUS_LABEL[item.status]}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-label">
                    {item.auftrag_nummer || "Ohne Projekt"}
                    {item.auftrag_beschreibung ? ` · ${item.auftrag_beschreibung}` : ""}
                  </div>
                  <div className="mt-0.5 text-sm text-label-secondary">
                    {item.contact_display || "Kein Ansprechpartner"}
                  </div>
                  {item.notes && (
                    <div className="mt-1 line-clamp-2 text-sm text-label-secondary">
                      {item.notes}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-label-tertiary">
                    <span>{item.created_by_name || item.created_by}</span>
                    <span>{formatDate(item.created_at)}</span>
                    {item.attachment_count > 0 && (
                      <span>
                        {item.attachment_count} Anhang{item.attachment_count === 1 ? "" : "e"}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AngebotDialog
        open={dialogOpen}
        onClose={closeDialog}
        onChanged={() => void reload()}
        agaId={activeId}
        canProcess={canProcess}
      />

      <MaDialog
        open={maDialogOpen}
        maId={activeMaId}
        canProcess={canProcess}
        onClose={() => {
          setMaDialogOpen(false);
          setActiveMaId(null);
        }}
        onChanged={() => void reload()}
      />

      <button
        type="button"
        className="material-mobile-fab sm:hidden"
        onClick={openNew}
        aria-label="Neue Angebotsanfrage"
      >
        <span aria-hidden>+</span>
        <span>Neue Anfrage</span>
      </button>
    </AppShell>
  );
}
